import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { ArrowUp, Paperclip } from "lucide-react";
import { SlashPalette, type SlashCommand } from "./SlashPalette";

// Composer — textarea + send button + slash palette overlay.
//   - BARE Enter sends; Ctrl/Cmd/Shift + Enter inserts a newline. The
//     chat convention here is "Enter = send" (matches Slack / ChatGPT
//     / WhatsApp); multi-line composition is the explicit modifier
//     case. Empty/whitespace-only submit is suppressed.
//   - Textarea auto-grows with content — `field-sizing: content` if the
//     browser supports it, otherwise a useLayoutEffect that mirrors the
//     scrollHeight. No max height; the panel body shrinks to keep the
//     composer fully visible if the user pastes a wall of text.
//   - `disabled` (set by parent while a turn is in flight) blocks send.
//   - When the draft starts with `/`, the SlashPalette opens above the
//     textarea. The palette extracts the filter token (text after `/`
//     up to whitespace) and renders matching commands. ArrowUp/Down +
//     Enter navigate + select; Esc closes. While the palette is open
//     the composer's Enter handler skips send so the palette's
//     window-level Enter listener gets to pick the command without
//     racing with a chat send.
//   - When the user picks a slash command without an argument, the
//     composer clears (assumes parent handles the action). When the
//     command takes an arg, the textarea is left alone so the parent
//     can read the typed arg.

interface Props {
    onSubmit: (body: string) => void;
    onSlashCommand?: (cmd: SlashCommand, arg?: string) => void;
    disabled?: boolean;
    placeholder?: string;
}

export function AssistantComposer({ onSubmit, onSlashCommand, disabled, placeholder }: Props) {
    const [value, setValue] = useState("");
    const taRef = useRef<HTMLTextAreaElement | null>(null);

    // `/` detection — open the slash palette when the draft starts with
    // `/` and has no embedded whitespace before the cursor (i.e. the
    // user is still typing the command token). Once a space appears the
    // palette closes so a trailing arg can be typed freely.
    const slashFilter = useMemo<string | null>(() => {
        if (!value.startsWith("/")) return null;
        const token = value.slice(1);
        const spaceIdx = token.indexOf(" ");
        if (spaceIdx >= 0) return null;
        return token;
    }, [value]);

    const send = useCallback(() => {
        const trimmed = value.trim();
        if (!trimmed) return;
        if (disabled) return;
        onSubmit(trimmed);
        setValue("");
    }, [value, disabled, onSubmit]);

    const handleSlashSelect = useCallback(
        (cmd: SlashCommand, arg?: string) => {
            onSlashCommand?.(cmd, arg);
            // Clear the composer regardless — slash commands are imperative,
            // not a chat message to the assistant.
            setValue("");
        },
        [onSlashCommand],
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            // Bare Enter sends; Ctrl/Cmd/Shift + Enter falls through to the
            // browser default (insert a newline). When the slash palette is
            // open, the palette owns Enter (it picks the highlighted
            // command); composer steps out of the way so the two don't
            // double-fire.
            if (e.key === "Enter" && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
                if (slashFilter !== null) return;
                e.preventDefault();
                send();
            }
        },
        [send, slashFilter],
    );

    // Auto-grow the textarea to match its content. Modern browsers (Chrome
    // 123+, Edge 123+, Safari 17+) honour `field-sizing: content` in CSS;
    // for everything else this layout-effect mirrors scrollHeight onto
    // height. useLayoutEffect (not useEffect) so the resize happens before
    // paint — no one-frame flash of a too-small textarea.
    useLayoutEffect(() => {
        const ta = taRef.current;
        if (!ta) return;
        ta.style.height = "auto";
        ta.style.height = `${ta.scrollHeight}px`;
    }, [value]);

    // Re-fit once after mount in case the textarea was hidden behind a
    // closed panel (zero scrollHeight before the panel opens).
    useEffect(() => {
        const ta = taRef.current;
        if (!ta) return;
        ta.style.height = "auto";
        ta.style.height = `${ta.scrollHeight}px`;
    }, []);

    return (
        <div className="assistant-composer">
            {slashFilter !== null && (
                <SlashPalette
                    filter={slashFilter}
                    currentDraft={value}
                    onSelect={handleSlashSelect}
                    onClose={() => setValue("")}
                />
            )}
            <textarea
                ref={taRef}
                className="assistant-composer__input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder ?? "Ask anything…  (Enter to send · Ctrl+Enter for new line)"}
                disabled={disabled}
                rows={1}
                data-testid="assistant-composer-input"
            />
            {/* Attachment slot — layout only; behaviour wires later. */}
            <button
                type="button"
                className="assistant-composer__attach"
                aria-label="Attach file"
                title="Attach file (coming soon)"
                data-testid="assistant-composer-attach"
            >
                <Paperclip size={14} />
            </button>
            <button
                type="button"
                className="assistant-composer__send"
                onClick={send}
                disabled={disabled || value.trim().length === 0}
                aria-label="Send"
                data-testid="assistant-composer-send"
            >
                <ArrowUp size={14} />
            </button>
        </div>
    );
}
